import classNames from 'classnames'
import type { FunctionComponent } from 'react'

import styles from './multi-copy-section.module.scss'
import { asColorLabel } from '@app-types/color'
import { Flex } from '@components/layout/grid'
import { Markdown } from '@components/markdown'
import { ActionButton } from '@contentful-components/action-button'
import type { MultiCopyItemFragment } from '@graphql/fragments/MultiCopySectionFragment.graphql'

const headingColors = ['future-fuscia', 'cyanora', 'coal-mine-canary']

export const MultiCopyItem: FunctionComponent<
    MultiCopyItemFragment & Props
> = ({
    heading,
    subheading,
    copy,
    button,
    image,
    background,
    coverImage,
    centered,
    maxHeadingLength = 100,
    index = 0,
}) => (
    <div
        className={classNames(
            styles['item-container'],
            {
                [styles['item-container--centered']]: centered,
            },
            styles[`background-${asColorLabel(background)}`],
        )}
    >
        {image?.source && (
            <img
                src={image.source2x}
                alt={image.alt ?? ''}
                loading='lazy'
                className={classNames(
                    styles.image,
                    !!coverImage && styles['image-with-cover'],
                )}
            />
        )}
        {!!coverImage && (
            <img
                src={coverImage.source2x}
                alt={coverImage.alt ?? ''}
                loading='lazy'
                className={classNames(styles['cover-image'])}
            />
        )}
        <Flex justify={centered ? 'center' : 'flex-start'}>
            <div
                className={classNames(
                    styles['content-container'],
                    !!coverImage && styles['content-container-with-cover'],
                )}
            >
                {!!heading && (
                    <div className={styles['item-heading']}>
                        <Markdown
                            tag='h4'
                            typeStyle={
                                maxHeadingLength > 30
                                    ? 'heading-sm'
                                    : 'heading-xl'
                            }
                            markdown={heading}
                            color={headingColors[index % headingColors.length]}
                        />
                    </div>
                )}
                {!!subheading && (
                    <Markdown
                        tag='h6'
                        typeStyle={{
                            _: 'body-lg',
                            md: 'body-xl',
                        }}
                        color='text'
                        markdown={`**${subheading}**`}
                    />
                )}
                {!!copy && (
                    <Markdown
                        markdown={copy}
                        typeStyle='body-lg'
                        color='dim'
                        boldColor='default'
                        accentColor={
                            heading && !subheading
                                ? headingColors[index % headingColors.length]
                                : 'primary'
                        }
                    />
                )}
            </div>
        </Flex>
        {!!button && (
            <div className={styles['button-container']}>
                <ActionButton {...button} />
            </div>
        )}
    </div>
)

interface Props {
    centered?: boolean
    maxHeadingLength?: number
    index?: number
}
